import { Box, Button, TextField, Typography } from '@mui/material';
import { SettingAccountIcon, SettingPasswordIcon, UserSettingIcon } from '~/assets/icons';
import styles from './AccountSetting.module.scss';
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import AccountSettingEmailDialog from '~/components/account-setting/dialogEmail/AccountSettingEmailDialog';
import { useState } from 'react';
import { DIALOG_MODE_EDIT } from '~/utils/constants/common';
import AccountSettingPasswordDialog from '~/components/account-setting/dialogPassword/AccountSettingPasswordDialog';
import ModalAlertDelete from '~/components/common/modal-alert-delete/ModalAlertDelete';
import { handleDeleteProfile, handleEditProfile } from '~/thunks/auth/authThunk';
import { notify } from '~/utils/helper';
import { StorageEnum } from '~/utils/enum';
import { adminRouteAbsolute } from '~/utils/constants/route';
import { useNavigate } from 'react-router-dom';
import ModalAlertConfirm from '~/components/common/modal-alert-confirm/ModalAlertConfirm';
import { PERMISSIONS } from '~/utils/constants/permission';
import usePermission from '~/hooks/usePermission';

const cx = classNames.bind(styles);
const AccountSetting = () => {
  //#region Declare Hook
  const { t } = useTranslation(['admin/account']);
  //#endregion Declare Hook
  const dispatch = useAppDispatch();
  //#region Selector
  const { profile } = useAppSelector((state) => state.auth);

  //#endregion Selector

  //#region Declare State
  const [openEditEmailDialog, setOpenEditEmailDialog] = useState<boolean>(false);
  const [openEditPasswordDialog, setOpenEditPasswordDialog] = useState<boolean>(false);
  const [openAlertDelete, setOpenAlertDelete] = useState<boolean>(false);
  const [openAlertDisable, setOpenAlertDisable] = useState<boolean>(false);
  const [editDialogMode, setEditDialogMode] = useState<string>('');
  const permissions = usePermission();
  const navigate = useNavigate();
  //#endregion Declare State

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleSetOpenEmailDialog = async (open: boolean, _needRefreshData?: boolean) => {
    setOpenEditEmailDialog(open);
  };
  const handleSetOpenPasswordDialog = async (open: boolean, _needRefreshData?: boolean) => {
    setOpenEditPasswordDialog(open);
  };

  const handleOpenEmailDialog = () => {
    setEditDialogMode(DIALOG_MODE_EDIT);
    setOpenEditEmailDialog(true);
  };

  const handleOpenPasswordDialog = () => {
    setEditDialogMode(DIALOG_MODE_EDIT);
    setOpenEditPasswordDialog(true);
  };

  const handleOpenAlertDelete = () => {
    setOpenAlertDelete(true);
  };

  const closeAlertDeleteModal = () => {
    setOpenAlertDelete(false);
  };

  const closeAlertDisableModal = () => {
    setOpenAlertDisable(false);
  };
  const handleOpenAlertDisable = () => {
    setOpenAlertDisable(true);
  };

  const hanleNavigateMenuAnchor = (url: string) => {
    navigate(url);
  };

  const onDeleteAccount = () => {
    dispatch(handleDeleteProfile())
      .unwrap()
      .then((successMessage) => {
        notify('success', successMessage);
        localStorage.removeItem(StorageEnum.ACCESS_TOKEN);
        localStorage.removeItem(StorageEnum.REFRESH_TOKEN);
        hanleNavigateMenuAnchor(adminRouteAbsolute.login);
        setOpenAlertDelete(false);
      })
      .catch((error) => {
        notify('error', error);
        setOpenAlertDelete(false);
      });
  };
  const onDisableAccount = () => {
    dispatch(handleEditProfile({ params: { isDisabled: true } }))
      .unwrap()
      .then((successMessage) => {
        notify('success', successMessage);
        localStorage.removeItem(StorageEnum.ACCESS_TOKEN);
        localStorage.removeItem(StorageEnum.REFRESH_TOKEN);
        hanleNavigateMenuAnchor(adminRouteAbsolute.login);
        setOpenAlertDisable(false);
      })
      .catch((error) => {
        notify('error', error);
        setOpenAlertDisable(false);
      });
  };

  //#endregion Handle Function
  return (
    <Box>
      <Box className={cx('title-cointainer')}>
        <SettingAccountIcon className={cx('setting-icon')} />
        <Typography variant='h6' gutterBottom className={cx('title')}>
          {t('settingAccount.accountSetting')}
        </Typography>
      </Box>

      <Box mb={3} mt={3} sx={{ width: '80%' }}>
        <Typography variant='body2' color='textSecondary' mb={2} className={cx('title-gmail')}>
          {t('settingAccount.email')}
        </Typography>
        <TextField fullWidth variant='outlined' value={profile.email} disabled />
        <Box display={'flex'} justifyContent={'space-between'} mt={1}>
          <Typography variant='body2' mt={1}>
            {t('settingAccount.yourEmail')} <strong>{profile.email}</strong>
          </Typography>
          {permissions[PERMISSIONS.auth_setting] && (
            <Button color='primary' sx={{ mt: 1 }} onClick={handleOpenEmailDialog}>
              {t('settingAccount.change')}
            </Button>
          )}
        </Box>
      </Box>
      {permissions[PERMISSIONS.auth_setting] && (
        <Box mb={3}>
          <Box display={'flex'}>
            <SettingPasswordIcon className={cx('settingPasswordIcon')} />
            <Typography variant='body2' color='textSecondary' className={cx('title-password')}>
              {t('settingAccount.password')}
            </Typography>
          </Box>

          <Button variant='contained' color='primary' sx={{ mt: 3 }} onClick={handleOpenPasswordDialog}>
            {t('settingAccount.changePassword')}
          </Button>
        </Box>
      )}
      <Box className='hidden' mt={5}>
        <Box display={'flex'}>
          <UserSettingIcon className={cx('userSettingIcon')} />
          <Typography variant='body2' color='textSecondary' className={cx('title-account')}>
            {t('settingAccount.disableDeleteAcc')}
          </Typography>
        </Box>

        <Box display='flex' gap={2} mt={2}>
          <Button variant='contained' color='primary' onClick={handleOpenAlertDisable}>
            {t('settingAccount.disableAccount')}
          </Button>
          <Button variant='contained' color='error' onClick={handleOpenAlertDelete}>
            {t('settingAccount.deleteAccount')}
          </Button>
        </Box>
      </Box>
      <AccountSettingEmailDialog
        open={openEditEmailDialog}
        setOpen={handleSetOpenEmailDialog}
        mode={editDialogMode}
        data={profile}
      />
      <AccountSettingPasswordDialog
        open={openEditPasswordDialog}
        setOpen={handleSetOpenPasswordDialog}
        mode={editDialogMode}
      />
      <ModalAlertDelete
        open={openAlertDelete}
        description={t('settingAccount.alertDescriptionDeleteAccount')}
        onCancel={closeAlertDeleteModal}
        onConfirm={onDeleteAccount}
      />
      <ModalAlertConfirm
        open={openAlertDisable}
        description={t('settingAccount.alertDescriptionDisableAccount')}
        confirm={t('confirmDisable', { ns: 'admin/common' })}
        onCancel={() => {
          closeAlertDisableModal();
        }}
        onConfirm={() => {
          onDisableAccount();
        }}
      />
    </Box>
  );
};

export default AccountSetting;
